/**
 * locationDisplay.js — Canonical bilingual resolver for free-text location
 * values stored on ads (`ad.city`, `ad.location`, composite strings).
 *
 * iter351 — Root fix for mixed-language location labels ("Gedarif City"
 * showing inside Arabic mode). Instead of a small hand-maintained city map,
 * this module builds a flat bilingual index from the FULL Country → Region →
 * City → Area catalog in `data/locations.js` (the same catalog the location
 * picker writes from), plus a legacy-alias table for old spellings.
 *
 * Language rules (per operator spec):
 *   • Arabic mode  → Arabic labels only. An unresolvable Latin token is
 *     DROPPED, never shown. If every token drops, the nearest Arabic parent
 *     (ad country) is shown instead; failing that the label is hidden.
 *   • English mode → English labels. Unresolvable tokens pass through as-is.
 *
 * Every listing surface (Home/Shop/Search/Ladies/Farmer/Favorites/AdDetails/
 * SellerProfile) resolves through `getLocalizedLocationLabel` (AdInfoCard)
 * or `getAdLocation` (adI18n) — both of which delegate here. Do NOT
 * reimplement location localization per page.
 */

import { COUNTRIES } from '../data/locations';

const LATIN_CHAR = /[A-Za-z]/;

/** Lowercase + strip diacritics + strip punctuation + collapse whitespace. */
function norm(value) {
  if (!value || typeof value !== 'string') return '';
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u064B-\u065F\u0670]/g, '') // Arabic harakat
    .replace(/[\u0300-\u036f]/g, '')       // Latin diacritics
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Flat bilingual index built once from the catalog. First-wins on key
// conflicts so primary-market (Sudan) entries take priority.
const INDEX = new Map();

function addEntry(en, ar) {
  if (!en && !ar) return;
  const entry = { en: en || '', ar: ar || '' };
  const enKey = norm(en);
  const arKey = norm(ar);
  if (enKey && !INDEX.has(enKey)) INDEX.set(enKey, entry);
  if (arKey && !INDEX.has(arKey)) INDEX.set(arKey, entry);
}

for (const country of COUNTRIES) {
  addEntry(country.countryName, country.countryNameAr);
  const codeKey = norm(country.countryCode);
  if (codeKey && !INDEX.has(codeKey)) {
    INDEX.set(codeKey, { en: country.countryName, ar: country.countryNameAr || '' });
  }
  for (const region of country.regions || []) {
    addEntry(region.name, region.ar);
    for (const city of region.cities || []) {
      addEntry(city.name, city.ar);
      for (const area of city.areas || []) {
        if (typeof area === 'string') addEntry(area, '');
        else if (area && typeof area === 'object') addEntry(area.name, area.ar);
      }
    }
  }
}

/**
 * Legacy spelling variants (old seeds, BigDataCloud transliterations,
 * user-reported forms) → normalized canonical catalog key.
 * Keys and values MUST be `norm()`-normalized strings.
 */
export const LEGACY_LOCATION_ALIASES = {
  // Gedaref variants (reported bug: "Gedarif City" leaking into AR mode)
  'gedarif': 'gedaref',
  'gedarif city': 'gedaref city',
  'gadarif': 'gedaref',
  'gadarif city': 'gedaref city',
  'al qadarif': 'gedaref',
  'al gadarif': 'gedaref',
  'el gadarif': 'gedaref',
  'al gedaref': 'gedaref',
  'el gedaref': 'gedaref',
  'qadarif': 'gedaref',
  // Khartoum North / Bahri
  'bahri': 'khartoum north',
  'khartoum bahri': 'khartoum north',
  'al khartum bahri': 'khartoum north',
  // BigDataCloud / old-seed transliterations
  'madani': 'wad madani',
  'wad medani': 'wad madani',
  'al ubayyid': 'el obeid',
  'al obeid': 'el obeid',
  'al fashir': 'el fasher',
  'al fasher': 'el fasher',
  'el fashir': 'el fasher',
  'portsudan': 'port sudan',
  'bur sudan': 'port sudan',
};

/**
 * Resolve one location token (any language, any known spelling) to its
 * bilingual `{ en, ar }` entry, or `null` when unknown.
 */
export function resolveLocationEntry(value) {
  const key = norm(value);
  if (!key) return null;
  const direct = INDEX.get(key);
  if (direct) return direct;
  const alias = LEGACY_LOCATION_ALIASES[key];
  if (alias) return INDEX.get(alias) || null;
  return null;
}

/**
 * Localize a raw location value (single or composite string) to the UI
 * language, enforcing the Arabic-mode no-English rule.
 *
 * @param {string} raw   e.g. "Gedarif City" or "Gedaref, Gedaref City"
 * @param {string} lang  'ar' | 'en' (i18n codes accepted)
 * @param {{countryCode?: string}} [opts]  ad country for parent fallback
 * @returns {string} localized label — '' means "hide the location line"
 */
export function localizeLocationValue(raw, lang = 'en', opts = {}) {
  if (!raw || typeof raw !== 'string') return '';
  const isAr = String(lang).toLowerCase().startsWith('ar');
  const sep = isAr ? '، ' : ', ';

  const tokens = raw.split(/[,،·|]+/).map((t) => t.trim()).filter(Boolean);
  const out = [];
  for (const tok of tokens) {
    const entry = resolveLocationEntry(tok);
    if (entry) {
      const label = isAr ? entry.ar : entry.en;
      if (label) {
        out.push(label);
      } else if (!isAr) {
        out.push(entry.en || entry.ar);
      }
      // AR mode with no AR label: drop token (parent/country covers it)
      continue;
    }
    // Unresolved free text: never show Latin inside Arabic mode
    if (isAr && LATIN_CHAR.test(tok)) continue;
    out.push(tok);
  }

  const deduped = [...new Set(out)];
  if (deduped.length) return deduped.join(sep);

  // Everything dropped → nearest parent: the ad's country in UI language
  const cc = (opts.countryCode || '').trim();
  if (cc) {
    const centry = resolveLocationEntry(cc);
    if (centry) return isAr ? (centry.ar || '') : (centry.en || '');
  }
  return '';
}
